import { BrowserPrintData } from "./a4";
import { base64ToBlob } from "@/utils/saveFile";

const PRINT_DIALOG_TIMEOUT_MS = 5 * 60 * 1000;

function waitForFrameLoad(frame: HTMLIFrameElement): Promise<void> {
  return new Promise((resolve) => {
    frame.addEventListener("load", () => resolve(), { once: true });
  });
}

const PAGE_ACTIVITY_EVENTS = ["focus", "pointermove", "keydown"] as const;

// Resolves once the dialog closes so a following print does not open on top
// of it. Chrome's PDF viewer never fires `afterprint`, so the operator's next
// interaction with the page (blocked while the dialog is open) counts too.
function waitForPrintDialogToClose(frameWindow: Window): Promise<boolean> {
  return new Promise((resolve) => {
    const finish = (closed: boolean) => {
      clearTimeout(timeout);
      frameWindow.removeEventListener("afterprint", onClose);
      for (const event of PAGE_ACTIVITY_EVENTS) {
        window.removeEventListener(event, onClose);
      }
      resolve(closed);
    };
    const onClose = () => finish(true);
    const timeout = setTimeout(() => finish(false), PRINT_DIALOG_TIMEOUT_MS);

    frameWindow.addEventListener("afterprint", onClose);
    for (const event of PAGE_ACTIVITY_EVENTS) {
      window.addEventListener(event, onClose);
    }
  });
}

/**
 * Opens the browser's print dialog for an A4 document from a hidden iframe,
 * so no print helper (QZ Tray) has to be installed.
 */
export async function printInBrowser(
  printData: BrowserPrintData,
): Promise<void> {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText =
    "position: fixed; right: 0; bottom: 0; width: 0; height: 0; border: 0; visibility: hidden;";

  const objectUrl =
    printData.type === "browser-pdf"
      ? URL.createObjectURL(
          base64ToBlob(printData.pdfBase64, "application/pdf"),
        )
      : null;

  const loaded = waitForFrameLoad(frame);
  if (objectUrl) {
    frame.src = objectUrl;
  } else if (printData.type === "browser-html") {
    frame.srcdoc = printData.html;
  }
  document.body.appendChild(frame);
  await loaded;

  const frameWindow = frame.contentWindow;
  if (!frameWindow) {
    frame.remove();
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    throw new Error("Print frame has no window");
  }

  frameWindow.print();

  // print() blocks until the dialog closes for HTML, but returns at once for
  // a PDF. Removing the frame while its dialog is open would cancel the job.
  const dialogClosed = objectUrl
    ? await waitForPrintDialogToClose(frameWindow)
    : true;

  if (dialogClosed) {
    frame.remove();
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}
